// amor.to — orchestration. One page, three moments: the welcome, the
// invitation, and the chamber where the crystal lives.

import {
  ackPulses,
  createSpace,
  fetchPulses,
  fetchSpaceState,
  joinSpace,
  loadCredentials,
  saveCredentials,
  sendPulse,
  type PulseRecord,
  type SpaceCredentials,
} from "./api.js";
import { GlassHarp, loadVoiceName, type VoiceName } from "./audio.js";
import { buildMesh, growShards, type Pulse } from "./crystal.js";
import { exportArtifact, exportSupported } from "./exporter.js";
import { Heartbeat } from "./haptics.js";
import { LiveLink } from "./live.js";
import { CrystalRenderer } from "./renderer.js";
import { TouchSurface } from "./touch.js";

const $ = <T extends HTMLElement>(selector: string): T => {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`missing element: ${selector}`);
  return el;
};

const screens = {
  welcome: $("#welcome"),
  invite: $("#invite"),
  join: $("#join"),
  chamber: $("#chamber"),
};

function show(name: keyof typeof screens): void {
  for (const [key, el] of Object.entries(screens)) {
    el.classList.toggle("visible", key === name);
  }
}

function whisper(text: string, holdMs = 4200): void {
  const el = $("#whisper");
  el.textContent = text;
  el.classList.add("visible");
  window.setTimeout(() => el.classList.remove("visible"), holdMs);
}

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Up to three 30°-bucket centers, most-touched first — what the drone hums.
function dominantHues(hues: number[]): number[] {
  const bins = new Map<number, number>();
  for (const h of hues) {
    const bin = Math.floor((((h % 360) + 360) % 360) / 30) * 30 + 15;
    bins.set(bin, (bins.get(bin) ?? 0) + 1);
  }
  return [...bins.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([bin]) => bin);
}

class Chamber {
  private renderer: CrystalRenderer;
  private harp = new GlassHarp();
  private heartbeat = new Heartbeat();
  private live: LiveLink;
  private pulses: Pulse[] = [];
  private pulseDays: { day: string; hue: number }[] = [];
  private lastDay = "";
  private lastMilestone = 0;
  private replaying = false;
  private lastPulseId = 0;
  private grown = 0;
  private growTarget = 0;
  private newest = -1;
  private newestGlow = 0;
  private lastFrameAt = 0;
  private angle = 0;
  private glow = 0;
  private glowTarget = 0.12;
  private holding = false;
  private partnerPresent = false;
  private surface: TouchSurface;

  constructor(private creds: SpaceCredentials) {
    this.renderer = new CrystalRenderer($<HTMLCanvasElement>("#crystal"));
    this.surface = new TouchSurface($("#stage"), {
      onStart: (s) => this.holdStart(s.hue, s.x, s.y),
      onMove: (s) => this.holdMove(s.hue, s.heldMs, s.x, s.y),
      onEnd: (s) => void this.holdEnd(s.hue, s.heldMs),
    });
    this.live = new LiveLink(creds.member_token, {
      onPresence: (others) => this.setPresence(others),
      onPartnerHoldStart: () => this.updateSync(),
      onPartnerHoldEnd: () => this.updateSync(),
      onPartnerPulse: () => void this.refreshPulses(true),
    });
    window.addEventListener("resize", () => this.renderer.resize());
    this.frame(performance.now());
  }

  async boot(): Promise<void> {
    const records = await fetchPulses(this.creds.member_token);
    this.absorb(records);
    this.rebuild();
    // Wake: the whole artifact grows in from the core over a few seconds.
    this.grown = reducedMotion ? this.pulses.length : 0;
    this.growTarget = this.pulses.length;
    const echoes = records.filter((r) => !r.mine && r.delivered_at === null);
    if (echoes.length) {
      whisper(echoes.length === 1 ? "one touch arrived while you were away" : `${echoes.length} touches arrived while you were away`);
      await this.playEchoes(echoes);
      await ackPulses(this.creds.member_token, echoes.map((e) => e.id));
    } else if (!records.length) {
      whisper("press and hold. stay a while.");
    }
    const state = await fetchSpaceState(this.creds.member_token);
    if (!state.paired) {
      whisper("the other half of this place is still unclaimed", 6000);
    }
  }

  private absorb(records: PulseRecord[]): void {
    for (const r of records) {
      if (r.id <= this.lastPulseId) continue;
      this.lastPulseId = r.id;
      const day = new Date(r.created_at * 1000).toISOString().slice(0, 10);
      this.pulseDays.push({ day, hue: r.hue });
      const firstLight = day !== this.lastDay;
      this.lastDay = day;
      this.pulses.push({
        hue: r.hue,
        duration_ms: r.duration_ms,
        amplitude: r.amplitude,
        mine: r.mine,
        kind: r.kind ?? "touch",
        firstLight,
      });
    }
  }

  private rebuild(): void {
    this.renderer.setMesh(buildMesh(growShards(this.creds.artifact_seed, this.pulses), this.pulses.length));
    // Dense artifacts dim slightly so additive overlaps stay luminous, not blown.
    this.renderer.setExposure(Math.max(0.6, Math.min(1.1, 1.15 - this.pulses.length * 0.006)));
    const recent = this.pulses.slice(-24).map((p) => p.hue);
    // The crystal hums the hues it's made of — quiet, and only after a
    // gesture has unlocked audio.
    this.harp.setDrone(dominantHues(recent));
    this.checkMilestone();
  }

  /** A new touch arrived (mine, live, or echo): grow toward it smoothly and
  let the newest shard settle with its own light. */
  private growTo(count: number): void {
    this.growTarget = count;
    this.newest = count;
    this.newestGlow = 1;
    if (reducedMotion) this.grown = count;
  }

  private checkMilestone(): void {
    const n = this.pulses.length;
    const milestones: Record<number, string> = {
      10: "ten touches. it knows you now.",
      50: "fifty touches held in the glass.",
      100: "one hundred. this is a place now.",
      500: "five hundred touches. few things are tended this long.",
      1000: "a thousand. the crystal remembers all of them.",
    };
    if (milestones[n] && n !== this.lastMilestone) {
      this.lastMilestone = n;
      whisper(milestones[n], 5200);
    }
  }

  /** Watch it grow: replay the whole history, shard by shard, riding the
  same continuous growth driver live touches use — no rebuild stutter. */
  async replayGrowth(): Promise<void> {
    if (this.replaying || !this.pulses.length) return;
    this.replaying = true;
    whisper("every touch, from the first", 4200);
    this.grown = 0;
    this.growTarget = 0;
    const step = Math.max(160, Math.min(450, 26000 / this.pulses.length));
    for (let i = 1; i <= this.pulses.length; i++) {
      this.growTarget = i;
      const p = this.pulses[i - 1];
      this.harp.playEcho(p.hue, p.duration_ms, p.amplitude * 0.7, { quick: true, deep: p.firstLight });
      this.bloomOnce(p.hue);
      await new Promise((r) => window.setTimeout(r, step));
    }
    this.replaying = false;
  }

  /** Average hue per day over the last 14 active days — the mood calendar. */
  moodCalendar(): { day: string; hue: number }[] {
    const byDay = new Map<string, number[]>();
    for (const { day, hue } of this.pulseDays) {
      const hues = byDay.get(day) ?? [];
      hues.push(hue);
      byDay.set(day, hues);
    }
    return [...byDay.entries()].slice(-14).map(([day, hues]) => {
      // Circular mean, so a day of 350° and 10° reads red, not green.
      let x = 0;
      let y = 0;
      for (const h of hues) {
        x += Math.cos((h * Math.PI) / 180);
        y += Math.sin((h * Math.PI) / 180);
      }
      return { day, hue: ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360 };
    });
  }

  private async refreshPulses(fromPartner: boolean): Promise<void> {
    const records = await fetchPulses(this.creds.member_token, this.lastPulseId);
    const fresh = records.filter((r) => r.id > 0);
    if (!fresh.length) return;
    this.absorb(fresh);
    this.rebuild();
    this.growTo(this.pulses.length);
    if (fromPartner) {
      const theirs = fresh.filter((r) => !r.mine);
      if (theirs.length) {
        // They're here right now — the touch lands live, no echo queue.
        const last = theirs[theirs.length - 1];
        this.harp.playEcho(last.hue, last.duration_ms, last.amplitude);
        this.heartbeat.echo(last.amplitude);
        this.bloomOnce(last.hue);
        await ackPulses(this.creds.member_token, theirs.map((t) => t.id));
      }
    }
  }

  private async playEchoes(echoes: PulseRecord[]): Promise<void> {
    for (const echo of echoes) {
      this.harp.playEcho(echo.hue, echo.duration_ms, echo.amplitude, { deep: echo.kind === "sync" });
      this.heartbeat.echo(echo.amplitude);
      this.bloomOnce(echo.hue);
      await new Promise((r) => window.setTimeout(r, 1500));
    }
  }

  private bloom(hue: number, alpha: number, x = 0.5, y = 0.62): void {
    const root = document.documentElement;
    root.style.setProperty("--bloom-hue", String(Math.round(hue)));
    root.style.setProperty("--bloom-alpha", alpha.toFixed(3));
    root.style.setProperty("--bloom-x", `${(x * 100).toFixed(1)}%`);
    root.style.setProperty("--bloom-y", `${(y * 100).toFixed(1)}%`);
  }

  private bloomOnce(hue: number): void {
    this.bloom(hue, 0.5);
    window.setTimeout(() => this.bloom(hue, 0), 900);
  }

  private get synced(): boolean {
    return this.holding && this.live.partnerHolding;
  }

  private updateSync(): void {
    document.body.classList.toggle("sync", this.synced);
    this.heartbeat.setSync(this.synced);
    if (this.synced) whisper("you are both here", 2600);
  }

  private setPresence(present: boolean): void {
    const arrived = present && !this.partnerPresent;
    this.partnerPresent = present;
    document.body.classList.toggle("present", present);
    if (arrived) whisper("they're here");
    this.updateSync();
  }

  private holdStart(hue: number, x: number, y: number): void {
    this.holding = true;
    this.harp.holdStart(hue);
    this.heartbeat.start(this.synced);
    this.live.sendHoldStart(hue);
    this.bloom(hue, 0.35, x, y);
    this.glowTarget = 0.5;
    this.updateSync();
  }

  private holdMove(hue: number, heldMs: number, x: number, y: number): void {
    if (!this.holding) return;
    this.harp.holdUpdate(hue, heldMs);
    const depth = Math.min(heldMs / 8000, 1);
    this.bloom(hue, 0.35 + depth * 0.4, x, y);
    this.glowTarget = 0.5 + depth * 0.5;
  }

  private async holdEnd(hue: number, heldMs: number): Promise<void> {
    if (!this.holding) return;
    const wasSynced = this.live.partnerHolding;
    this.holding = false;
    this.harp.holdEnd();
    this.heartbeat.stop();
    this.live.sendHoldEnd();
    this.bloom(hue, 0);
    this.glowTarget = 0.12;
    this.updateSync();
    if (heldMs < 150) return; // a graze, not a touch
    const duration = Math.round(Math.min(heldMs, 8000));
    const amplitude = Math.min(heldMs / 4000, 1);
    // Released while they were holding too → a sync touch, and the crystal
    // mints a golden twin spire no one can grow alone.
    const kind: "touch" | "sync" = wasSynced ? "sync" : "touch";
    const day = new Date().toISOString().slice(0, 10);
    const firstLight = day !== this.lastDay;
    this.lastDay = day;
    this.pulseDays.push({ day, hue });
    // The crystal takes the energy immediately; the network follows.
    this.pulses.push({ hue, duration_ms: duration, amplitude, mine: true, kind, firstLight });
    this.rebuild();
    this.growTo(this.pulses.length);
    if (kind === "sync") whisper("grown together — a golden spire", 4600);
    try {
      const saved = await sendPulse(this.creds.member_token, { hue, duration_ms: duration, amplitude, kind });
      this.lastPulseId = Math.max(this.lastPulseId, saved.id);
      this.live.sendPulseNotice();
    } catch {
      whisper("that touch couldn't be kept — it will fade on reload", 5000);
    }
  }

  private frame = (now: number): void => {
    const dt = Math.min((now - (this.lastFrameAt || now)) / 1000, 0.1);
    this.lastFrameAt = now;
    const breath = reducedMotion ? 0.5 : Math.sin(now / 4200) * 0.5 + 0.5;
    const altar = document.body.classList.contains("altar");
    if (!reducedMotion || this.holding) this.angle += this.holding ? 0.0009 : altar ? 0.0007 : 0.0018;
    this.glow += (this.glowTarget - this.glow) * 0.06;
    // Continuous growth: emergence pace scales with backlog so a boot
    // wake-up finishes in a few seconds while a single touch takes ~1.5s.
    const backlog = this.growTarget - this.grown;
    if (backlog > 0) {
      const pace = Math.max(0.7, backlog / 4);
      this.grown = Math.min(this.growTarget, this.grown + dt * pace);
    }
    this.newestGlow = Math.max(0, this.newestGlow - dt * 0.45);
    this.renderer.setGrowth(this.grown, this.newest, reducedMotion ? 0 : this.newestGlow);
    if (this.holding) {
      const s = this.surface.current;
      this.holdMove(s.hue, s.heldMs, s.x, s.y);
    }
    this.renderer.render(this.angle, this.glow, breath);
    requestAnimationFrame(this.frame);
  };

  async exportFilm(): Promise<void> {
    whisper("holding still for the portrait…", 7000);
    await exportArtifact(this.renderer);
  }

  setVoice(name: VoiceName): void {
    this.harp.setVoice(name);
    this.harp.playEcho(300, 1200, 0.4, { quick: true });
  }

  pulseCount(): number {
    return this.pulses.length;
  }
}

const THEME_KEY = "amor.theme";
const THEMES: { name: string; swatch: string }[] = [
  { name: "midnight", swatch: "#221c30" },
  { name: "dawn", swatch: "#3a2b27" },
  { name: "sea", swatch: "#1c2a3d" },
  { name: "moss", swatch: "#22321f" },
  { name: "ember", swatch: "#3c2415" },
];

function applyTheme(name: string): void {
  if (name === "midnight") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = name;
  localStorage.setItem(THEME_KEY, name);
  document.querySelectorAll<HTMLButtonElement>(".theme-dot").forEach((d) => {
    d.classList.toggle("active", d.dataset.themeName === name);
  });
}

function wireThemes(): void {
  const row = $("#theme-row");
  for (const t of THEMES) {
    const dot = document.createElement("button");
    dot.className = "theme-dot";
    dot.style.background = t.swatch;
    dot.dataset.themeName = t.name;
    dot.setAttribute("aria-label", `${t.name} chamber`);
    dot.addEventListener("click", () => applyTheme(t.name));
    row.appendChild(dot);
  }
  applyTheme(localStorage.getItem(THEME_KEY) ?? "midnight");
}

function wireVoices(chamber: Chamber): void {
  const chips = document.querySelectorAll<HTMLButtonElement>(".voice-chip");
  const mark = (name: string) => chips.forEach((c) => c.classList.toggle("active", c.dataset.voice === name));
  mark(loadVoiceName());
  chips.forEach((chip) => {
    chip.addEventListener("click", () => {
      const name = chip.dataset.voice as VoiceName;
      chamber.setVoice(name);
      mark(name);
    });
  });
}

// Altar mode: the chamber stays lit on a shelf or nightstand — screen awake,
// turning slowly, no words. A quiet object in the room that is also them.
let wakeLock: { release(): Promise<void> } | null = null;

async function toggleAltar(): Promise<void> {
  const on = document.body.classList.toggle("altar");
  const button = $("#settings-altar");
  button.textContent = on ? "return from the altar" : "leave it glowing";
  if (on) {
    whisper("it will stay lit", 3600);
    try {
      const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<never> } };
      wakeLock = (await nav.wakeLock?.request("screen")) ?? null;
    } catch {
      wakeLock = null; // no wake lock — altar mode still dims and turns
    }
  } else if (wakeLock) {
    await wakeLock.release().catch(() => undefined);
    wakeLock = null;
  }
}

function wireSettings(chamber: Chamber, inviteUrl: string | null): void {
  const veil = $("#settings");
  const corner = $("#corner");
  let timer: number | null = null;
  const open = () => {
    $("#touch-count").textContent =
      chamber.pulseCount() === 1 ? "one touch held so far" : `${chamber.pulseCount()} touches held so far`;
    $("#settings-invite").classList.toggle("hidden", !inviteUrl);
    $("#settings-export").classList.toggle("hidden", !exportSupported());
    $("#settings-replay").classList.toggle("hidden", chamber.pulseCount() === 0);
    // Mood calendar: the last fourteen days of this place, one dot each.
    const calendar = $("#mood-calendar");
    calendar.replaceChildren(
      ...chamber.moodCalendar().map(({ day, hue }) => {
        const dot = document.createElement("span");
        dot.className = "mood-dot";
        dot.style.background = `hsl(${Math.round(hue)} 62% 58%)`;
        dot.title = day;
        return dot;
      }),
    );
    veil.classList.add("visible");
  };
  corner.addEventListener("pointerdown", () => {
    timer = window.setTimeout(open, 700);
  });
  const cancel = () => {
    if (timer !== null) window.clearTimeout(timer);
  };
  corner.addEventListener("pointerup", cancel);
  corner.addEventListener("pointercancel", cancel);
  $("#settings-close").addEventListener("click", () => veil.classList.remove("visible"));
  $("#settings-export").addEventListener("click", () => {
    veil.classList.remove("visible");
    void chamber.exportFilm();
  });
  $("#settings-replay").addEventListener("click", () => {
    veil.classList.remove("visible");
    void chamber.replayGrowth();
  });
  $("#settings-altar").addEventListener("click", () => {
    veil.classList.remove("visible");
    void toggleAltar();
  });
  wireThemes();
  wireVoices(chamber);
  if (inviteUrl) {
    $("#settings-invite").addEventListener("click", () => void (async () => {
      await navigator.clipboard.writeText(inviteUrl);
      whisper("invitation copied. it works once.");
      veil.classList.remove("visible");
    })());
  }
}

async function enterChamber(creds: SpaceCredentials, inviteUrl: string | null): Promise<void> {
  show("chamber");
  const chamber = new Chamber(creds);
  wireSettings(chamber, inviteUrl);
  await chamber.boot();
}

async function boot(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const inviteToken = params.get("i");
  const existing = loadCredentials();
  // Undocumented: preview another species without a new space (?species_seed=N).
  const devSeed = params.get("species_seed");
  if (existing && devSeed !== null && /^\d+$/.test(devSeed)) {
    existing.artifact_seed = Number(devSeed);
  }

  if (inviteToken && !existing) {
    show("join");
    $("#join-enter").addEventListener("click", () => void (async () => {
      try {
        const creds = await joinSpace(inviteToken);
        saveCredentials(creds);
        history.replaceState(null, "", location.pathname);
        await enterChamber(creds, null);
      } catch {
        $("#join-copy").textContent = "this invitation was already opened, or never existed.";
        $("#join-enter").classList.add("hidden");
      }
    })());
    return;
  }

  if (existing) {
    await enterChamber(existing, sessionStorage.getItem("amor.invite_url"));
    return;
  }

  show("welcome");
  $("#begin").addEventListener("click", () => void (async () => {
    const created = await createSpace();
    saveCredentials(created);
    const inviteUrl = created.invite_url.startsWith("http")
      ? created.invite_url.replace(/^https?:\/\/[^/]+/, location.origin)
      : created.invite_url;
    sessionStorage.setItem("amor.invite_url", inviteUrl);
    $("#invite-link").textContent = inviteUrl;
    show("invite");
    $("#invite-copy").addEventListener("click", () => void (async () => {
      await navigator.clipboard.writeText(inviteUrl);
      $("#invite-copy").textContent = "copied";
    })());
    $("#invite-enter").addEventListener("click", () => {
      void enterChamber(created, inviteUrl);
    });
  })());
}

void boot();
